import { useEffect, useState } from 'react';
import { InboxIcon } from '@heroicons/react/24/outline';
import { Panel } from '../../../../components/panels/Panel';
import { ZoomableImageViewport } from '../../components/ZoomableImageViewport';
import { Modal } from '../../../../components/modals/Modal';
import { groupVeralliaDashboardDetections } from '../../../../services/dashboard/verallia-dashboard-grouping-service';
import { getVeralliaDashboardDefectLabel, sortVeralliaDashboardDetections } from '../../../../services/dashboard/verallia-dashboard-service';

const maskCache = new Map();

const parseSegmentationMask = (text) => {
  const values = text.trim().split(/\s+/).map(Number);
  if (values.length < 6 || values.length % 2 !== 0 || values.some((value) => !Number.isFinite(value) || value < 0 || value > 1)) return null;
  return Array.from({ length: values.length / 2 }, (_, index) => [values[index * 2], values[index * 2 + 1]]);
};

const loadSegmentationMask = (url) => {
  if (!maskCache.has(url)) {
    const promise = fetch(url).then((response) => {
      if (!response.ok) throw new Error(`Mask unavailable: ${response.status}`);
      return response.text();
    }).then(parseSegmentationMask).then((points) => {
      if (!points) maskCache.delete(url);
      return points;
    }).catch(() => { maskCache.delete(url); return null; });
    maskCache.set(url, promise);
  }
  return maskCache.get(url);
};

const getMaskUrl = (imageUrl) => /\.jpe?g(?=[?#]|$)/i.test(imageUrl) ? imageUrl.replace(/\.jpe?g(?=[?#]|$)/i, '.txt') : null;

const DashboardMaskedPhoto = ({ photos, masks, alt, className = '', showMask = true, modal = false }) => {
  const [photoIndex, setPhotoIndex] = useState(0);
  const [size, setSize] = useState(null);
  const [status, setStatus] = useState('loading');
  const photoKey = photos.join('|');
  const currentUrl = photos[photoIndex];

  useEffect(() => { setPhotoIndex(0); setSize(null); setStatus('loading'); }, [photoKey]);

  const handleError = () => {
    setSize(null);
    if (photoIndex + 1 < photos.length) setPhotoIndex((index) => index + 1);
    else setStatus('missing');
  };

  const imageContent = (
    <>
      {currentUrl && <img key={currentUrl} src={currentUrl} alt={alt} draggable={false} className={`h-full w-full object-contain ${status === 'ready' ? '' : 'invisible'}`} onLoad={(event) => { setSize({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight }); setStatus('ready'); }} onError={handleError}/>}
      {status === 'ready' && showMask && size && masks.length > 0 && (
        <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox={`0 0 ${size.width} ${size.height}`} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          {masks.map((points, index) => <polygon key={index} points={points.map(([x, y]) => `${x * size.width},${y * size.height}`).join(' ')} fill="rgba(239, 68, 68, 0.30)" stroke="rgba(248, 113, 113, 0.98)" strokeWidth="2" vectorEffect="non-scaling-stroke"/>)}
        </svg>
      )}
    </>
  );

  return (
    <div className={`relative overflow-hidden bg-perception-black-900 ${className}`} style={modal && size ? { aspectRatio: `${size.width} / ${size.height}` } : undefined}>
      {modal ? <ZoomableImageViewport resetKey={currentUrl}>{imageContent}</ZoomableImageViewport> : imageContent}
      {status === 'missing' && <div className="absolute inset-0 flex items-center justify-center px-3 text-center text-xs text-perception-gray-500">La imagen estará disponible pronto.</div>}
    </div>
  );
};

export const VeralliaDashboardKpi = ({ label, value }) => (
  <Panel noHeader rounded category="secondary">
    <div className="flex flex-col gap-3">
      <div className="text-sm text-perception-gray-600">{label}</div>
      <div className="text-3xl text-perception-gray-300">{value}</div>
    </div>
  </Panel>
);

export const VeralliaDashboardNoData = ({ text = 'Sin datos' }) => (
  <div className="flex min-h-40 flex-col items-center justify-center gap-3 text-perception-gray-700">
    <InboxIcon className="size-12"/>
    <div className="text-sm">{text}</div>
  </div>
);

export const VeralliaDashboardDetectionImage = ({ src, sources = [], alt, className = '' }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showMask, setShowMask] = useState(true);
  const photos = [...new Set([src, ...sources].filter((url) => typeof url === 'string' && url.length > 0))];
  const maskUrls = [...new Set(photos.map(getMaskUrl).filter(Boolean))];
  const maskKey = maskUrls.join('|');
  const [masks, setMasks] = useState([]);

  useEffect(() => {
    let active = true;
    setMasks([]);
    Promise.all(maskUrls.map(loadSegmentationMask)).then((result) => { if (active) setMasks(result.filter(Boolean)); });
    return () => { active = false; };
  }, [maskKey]);

  return (
    <>
      <button type="button" className={`overflow-hidden rounded-md border border-perception-black-800 hover:border-perception-blue hover:ring-1 hover:ring-inset hover:ring-perception-blue ${className}`} onClick={() => { setShowMask(true); setIsModalOpen(true); }}>
        <DashboardMaskedPhoto photos={photos} masks={masks} alt={alt} className="h-full w-full"/>
      </button>
      <Modal isOpen={isModalOpen} setIsOpen={setIsModalOpen} size="large" theme="dark">
        <div className="flex w-full flex-col gap-3">
          <DashboardMaskedPhoto key={isModalOpen ? 'open' : 'closed'} photos={photos} masks={masks} alt={alt} className="mx-auto aspect-video max-h-[76vh] w-full rounded-md" showMask={showMask} modal/>
          <div className="flex justify-end">
            <button type="button" role="switch" aria-checked={showMask} disabled={masks.length === 0} onClick={() => setShowMask((value) => !value)} className="flex items-center gap-2 text-xs text-perception-gray-400 disabled:cursor-not-allowed disabled:opacity-40">
              Mostrar máscara
              <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${showMask ? 'bg-perception-blue' : 'bg-perception-black-500'}`}><span className={`inline-block size-4 rounded-full bg-white transition-transform ${showMask ? 'translate-x-[18px]' : 'translate-x-[2px]'}`}/></span>
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export const VeralliaDashboardPictureWall = ({ detections, title = 'Fotos del turno' }) => {
  const groupedDetections = sortVeralliaDashboardDetections(groupVeralliaDashboardDetections(detections));

  return (
    <Panel noHeader rounded category="secondary" overflowAuto={false} className="h-[420px]">
      <div className="mb-4 flex shrink-0 items-center justify-between">
        <div className="text-sm font-semibold uppercase text-perception-gray-400">{title}</div>
        <div className="text-xs text-perception-gray-600">{groupedDetections.length} grupos</div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {groupedDetections.length === 0 ? <VeralliaDashboardNoData/> : (
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4 2xl:grid-cols-6">
            {groupedDetections.map((detection) => (
              <div key={detection.id} className="overflow-hidden rounded-md bg-perception-black-700">
                <VeralliaDashboardDetectionImage src={detection.image} sources={detection.imageSources} alt={detection.palletId} className="aspect-video w-full"/>
                <div className="flex flex-col gap-1 p-2 text-xs">
                  <div className="font-medium text-perception-gray-300">{detection.palletId}</div>
                  <div className="text-perception-gray-600">{detection.line} · {getVeralliaDashboardDefectLabel(detection.defect)}{detection.detectionCount > 1 ? ` · ${detection.detectionCount} capturas` : ''}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
};
